import {
  ArrowCircleLeftOutlined,
  ArrowCircleRightOutlined,
  AutoFixHigh,
  ExpandMore,
  Settings,
  TipsAndUpdatesOutlined,
} from '@mui/icons-material';
import {
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { BubbleMenu, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { FormApi } from 'final-form';
import { makeValidate, Switches, TextField } from 'mui-rff';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { CustomFile } from 'Types/file';
import * as Yup from 'yup';

import FileCard from '../../components/Chat/FileCard';
import FileViewDialog from '../../components/Chat/FileViewDialog';
import ConfirmationDialog from '../../components/DealGPT/PhaseTwo/ConfirmationDialog';
import LoadingMask from '../../components/LoadingMask';
import {
  AI_APP_STORE_LABEL,
  EXTERNAL_RESOURCE_LABEL,
  HIDDEN_PROMPT_FORM_TAGS,
  INTELLIO_ADVANTAGE_LABEL,
  SYSTEM_MANAGED_PROMPT_TAGS,
} from '../../constants/promptLabels';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { useFetchData } from '../../hooks/useFetchData';
import { copyFile, deleteFile, postFile, updateFile } from '../../redux/actions/fileActions';
import {
  archivePrompt,
  createPrompt,
  createPromptAdmin,
  updatePrompt,
  updatePromptAdmin,
} from '../../redux/actions/promptActions';
import { myFolder, PROMPT_DEFAULTS } from '../../redux/consts';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { selectUsers } from '../../redux/reducers/userReducer';
import { AppDispatch } from '../../redux/store';
import { generateRecommendationPrompt } from '../../services/promptTypes';
import {
  LinkDiv,
  PromptEditHelperText,
  PromptEditTiptapLabel,
  PromptEditTiptapStyled,
  PromptEditTiptapWrapper,
  StyledAccordion,
} from '../../theme/CustomComponents';
import { AppEnum, PromptView, SolutionType, ToolName } from '../../Types/enums';
import { LlmEnum } from '../../Types/llm';
import { CreatePrompt, PatchPromptOpts, Prompt, PromptLabel, PromptType } from '../../Types/prompt';
import { getFormattedDBTimestamp } from '../../util/dateUtils';
import { PromptLabelDefinitionsPathUrl, PromptTypesPathUrl } from '../../util/routesUrls';
import { ToolConfig } from '../../util/tools';
import { openSafeExternalLink } from '../../util/url';
import LearnMore from '../LearnMore';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useAuth } from '../Providers/AuthProvider';
import { useDrawer } from '../Providers/DrawerProvider';
import { useFeatures } from '../Providers/FeatureProvider';
import TemperatureSelector from '../TemperatureSelector';
import ManagePromptTypesDialog from './ManagePromptTypesDialog';
import ModificationConfirmationDialog from './ModificationConfirmationDialog';
import PromptFileUpload from './PromptFileUpload';
import PromptFormSkeleton from './PromptFormSkeleton';
import PromptToolsSelector from './PromptToolsSelector';

const errorMessageRequired = 'This field is required.';
const errorMessageInvalidUrl =
  'Please enter a valid URL that starts with http:// or https://. Example: https://www.example.com';

const createSchema = (external?: boolean) => {
  const baseSchema = {
    title: Yup.string().required(errorMessageRequired),
    description: Yup.string().required(errorMessageRequired),
    labels: Yup.array().of(Yup.string()).required(errorMessageRequired),
    ownerId: Yup.string().required(errorMessageRequired),
    solutionType: Yup.string().oneOf(Object.values(SolutionType)).required(errorMessageRequired),
  };

  return Yup.object({
    ...baseSchema,
    ...(external && {
      externalLink: Yup.string()
        .required(errorMessageRequired)
        .test('valid-url', errorMessageInvalidUrl, (value) => {
          if (!value) return false;
          try {
            const url = new URL(value);
            return url.protocol === 'http:' || url.protocol === 'https:';
          } catch {
            return false;
          }
        }),
    }),
  });
};

export interface PromptFormProps {
  promptId: string | undefined;
  promptView: PromptView | undefined;
  originalPrompt: Prompt | undefined;
  isAIAppStore?: boolean;
  external?: boolean;
  stepBuilderPromptText?: string | undefined;
  onNewPrompt?: (prompt: Prompt) => void;
  onEditPrompt?: (prompt: Prompt) => void;
}

const PromptForm: React.FC<PromptFormProps> = ({
  promptId,
  promptView,
  originalPrompt,
  isAIAppStore,
  external,
  stepBuilderPromptText = '',
  onNewPrompt,
  onEditPrompt,
}) => {
  const { selectedApp } = useSelector(selectAppStatus);
  const { llm } = useSelector(selectAppStatus);
  const { currentUser, isAdmin } = useAuth();
  const { closeDrawer } = useDrawer();
  const analytics = useAnalytics();
  const features = useFeatures();
  const navigate = useClassicNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { currentFolder } = useSelector(selectFolders);
  const { users } = useSelector(selectUsers);

  const entity = useMemo(() => (isAIAppStore ? 'app' : 'prompt'), [isAIAppStore]);

  const validate = useMemo(() => {
    const schema = createSchema(isAIAppStore || external);
    return makeValidate(schema);
  }, [isAIAppStore, external]);

  const shouldSaveCopyRef = useRef(false);
  const permanentTags = useMemo<string[]>(() => [...SYSTEM_MANAGED_PROMPT_TAGS], []);

  const toolOptions = useMemo(() => {
    // Helper to filter out STRACL_MCP if features.stracl is false
    const filterTools = (tools: ToolName[]) =>
      features.stracl ? tools : tools.filter((tool) => tool !== ToolName.STRACL_MCP);

    if (isAdmin || features.show_tools) {
      return filterTools(Object.values(ToolName)).map((tool) => ({
        value: tool,
        label: ToolConfig[tool].name,
      }));
    } else {
      return filterTools([ToolName.DEEP_RESEARCH, ToolName.WEB_SEARCH]).map((tool) => ({
        value: tool,
        label: ToolConfig[tool].name,
      }));
    }
  }, [isAdmin, features.show_tools, features.stracl]);

  const [isModificationConfirmationOpen, setIsModificationConfirmationOpen] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<CreatePrompt>(PROMPT_DEFAULTS);
  const [formKey, setFormKey] = useState(0);
  const [showFeatured, setShowFeatured] = useState(currentPrompt?.published);
  const [modalTile, setModalTitle] = useState<string>(
    `Are you sure you want to update this published ${entity}?`
  );

  const [isEditing, setIsEditing] = useState(!promptId);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [editingIsRequired, setEditingIsRequired] = useState(false);
  const [selectedText, setSelectedText] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<CustomFile[]>([]);
  const [fileToView, setFileToView] = useState<CustomFile | undefined>();
  const [fileViewIndex, setFileViewIndex] = useState<number | undefined>();

  const handleDeleteChip = (index: number) => {
    setSelectedText((prevChips) => prevChips.filter((_, i) => i !== index));
  };
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [currentRecommendationIndex, setCurrentRecommendationIndex] = useState<number | null>(null);
  const [isGeneratingRecommendation, setIsGeneratingRecommendation] = useState(false);
  const [selectedPromptType, setSelectedPromptType] = useState<PromptType | null>(null);
  const [isManagePromptTypeOpen, setIsManagePromptTypeOpen] = useState(false);
  const { data: promptTypes, refetch: refetchPromptTypes } = useFetchData<PromptType[]>({
    urlPath: PromptTypesPathUrl(),
    skip: !isAdmin,
  });
  const { data: promptLabels } = useFetchData<PromptLabel[]>({
    urlPath: PromptLabelDefinitionsPathUrl(),
  });
  const initialPromptValues = useMemo(() => {
    const ownerId = currentPrompt.ownerId ?? currentUser?.id ?? '';

    return {
      ...PROMPT_DEFAULTS,
      ...currentPrompt,
      ownerId,
      solutionType: currentPrompt.solutionType ?? SolutionType.PROMPT,
    };
  }, [currentPrompt, currentUser?.id]);

  const userOptions = useMemo(
    () => [...users].sort((left, right) => left.preferredName.localeCompare(right.preferredName)),
    [users]
  );
  const solutionTypeOptions = useMemo(() => Object.values(SolutionType), []);

  const isAtFirstRecommendation = currentRecommendationIndex === 0;
  const isAtLastRecommendation =
    currentRecommendationIndex !== null &&
    currentRecommendationIndex === recommendations.length - 1;

  const isExternalPrompt = useMemo(
    () => external || Boolean(originalPrompt?.externalLink),
    [originalPrompt, external]
  );

  const tagOptions = useMemo(
    () =>
      [...new Set((promptLabels ?? []).map((label) => label.title))].filter(
        (tag) => !HIDDEN_PROMPT_FORM_TAGS.includes(tag as (typeof HIDDEN_PROMPT_FORM_TAGS)[number])
      ),
    [promptLabels]
  );

  const editor = useEditor({
    extensions: [StarterKit, TextStyle, Color],
    shouldRerenderOnTransaction: false,
    content: stepBuilderPromptText ?? '',
    autofocus: true,
  });

  const addSelectedVariable = useCallback(() => {
    if (!editor) return;

    const selection = window.getSelection();
    const selectedTextRaw = selection?.toString().trim() || '';
    if (!selectedTextRaw) return;

    const editorText = editor.getText();
    const lowerSelectedText = selectedTextRaw.toLowerCase();

    // Escapes regex special characters
    const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Helper: Checks if `text` is a whole word in `content`
    const isWholeWord = (text: string, content: string): boolean => {
      const pattern = new RegExp(`\\b${escapeRegExp(text)}\\b`, 'i');
      return pattern.test(content);
    };

    if (
      selectedTextRaw &&
      !selectedText.includes(lowerSelectedText) &&
      isWholeWord(selectedTextRaw, editorText)
    ) {
      setSelectedText((prev) => [...prev, selectedTextRaw]);

      // Clear selection
      selection?.removeAllRanges();

      // Restore cursor at current position
      const { from } = editor.state.selection;
      editor.chain().setTextSelection(from).run();
    } else {
      enqueueSnackbar('Incorrect text selection for a variable', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  }, [editor, selectedText]);

  const setEditorContent = useCallback(
    (content: string) => {
      if (editor) {
        editor.commands.setContent(content?.replaceAll('\n', '</br>'));
      }
    },
    [editor]
  );

  const processFiles = useCallback(
    async (
      files: File[] | CustomFile[],
      isClone: boolean
    ): Promise<{ processed: CustomFile[]; hadFailures: boolean }> => {
      const filesJustProcessed: CustomFile[] = [];
      let hadFailures = false;

      if (files.length === 0) return { processed: filesJustProcessed, hadFailures };

      for (const file of files) {
        try {
          const action = isClone
            ? await dispatch(
                copyFile({
                  fileId: (file as CustomFile).id,
                  folderId: undefined,
                  promptId: undefined,
                })
              )
            : await dispatch(
                postFile({
                  formData: (() => {
                    const formData = new FormData();
                    formData.append('file', file as File);
                    return formData;
                  })(),
                  folderId: undefined,
                  promptId: undefined,
                })
              );

          const messageFile = action.payload as CustomFile;

          if (messageFile.statusText) {
            hadFailures = true;
            const getFileName = (file: File | CustomFile) =>
              'fileName' in file ? file.fileName : 'name' in file ? file.name : 'Unknown file';

            const getFileSize = (file: File | CustomFile) =>
              'size' in file ? file.size : undefined;

            setUploadedFiles((prev) =>
              prev.filter((f) => {
                const fName = getFileName(f);
                const fSize = getFileSize(f);
                const targetName = getFileName(file);
                const targetSize = getFileSize(file);
                return !(fName === targetName && fSize === targetSize);
              })
            );

            const fileLabel = getFileName(file);

            enqueueSnackbar(`${fileLabel} failed to upload.`, {
              variant: 'error',
              autoHideDuration: null,
            });

            continue;
          }

          filesJustProcessed.push(messageFile);
        } catch (err) {
          hadFailures = true;
          continue;
        }
      }

      return { processed: filesJustProcessed, hadFailures };
    },
    [dispatch, setUploadedFiles]
  );

  const saveCurrentEditorToRecommendation = useCallback(() => {
    if (
      currentRecommendationIndex !== null &&
      editor &&
      currentRecommendationIndex >= 0 &&
      currentRecommendationIndex < recommendations.length
    ) {
      const updatedPrompt = editor.getText().trim();
      setRecommendations((prev) => {
        const updated = [...prev];
        updated[currentRecommendationIndex] = updatedPrompt;
        return updated;
      });
    }
  }, [currentRecommendationIndex, editor, recommendations]);

  const createRecommendation = useCallback(async () => {
    if (!editor) return;

    const promptText = editor.getText().trim();
    if (!promptText) {
      enqueueSnackbar('Prompt is empty. Please enter text to get recommendations.', {
        variant: 'warning',
      });
      return;
    }

    // Ensure the original prompt is saved as the first recommendation
    if (recommendations.length === 0) {
      setRecommendations([promptText]);
      setCurrentRecommendationIndex(0);
    }

    saveCurrentEditorToRecommendation();
    setIsGeneratingRecommendation(true);

    try {
      const generated = await generateRecommendationPrompt(promptText);

      if (!generated) {
        enqueueSnackbar('Unable to generate recommendation', { variant: 'error' });
      } else {
        setRecommendations((prev) => {
          const updated = [...prev, generated];
          setCurrentRecommendationIndex(updated.length - 1);
          return updated;
        });
        setEditorContent(generated);
      }
    } catch (error) {
      const message =
        typeof error === 'string' ? error : error instanceof Error ? error.message : String(error);
      if (message?.includes('Unable to generate recommendation')) {
        enqueueSnackbar(message, { variant: 'error' });
      } else {
        enqueueSnackbar('Something went wrong while generating a recommendation', {
          variant: 'error',
        });
      }
    } finally {
      setIsGeneratingRecommendation(false);
    }
  }, [editor, recommendations.length, setEditorContent, saveCurrentEditorToRecommendation]);

  const navigateToRecommendation = useCallback(
    (direction: 'next' | 'previous') => {
      if (currentRecommendationIndex === null || !editor) return;

      const isNext = direction === 'next';
      const newIndex = currentRecommendationIndex + (isNext ? 1 : -1);

      const isValidIndex = newIndex >= 0 && newIndex < recommendations.length;
      if (!isValidIndex) return;

      saveCurrentEditorToRecommendation();
      setCurrentRecommendationIndex(newIndex);
      editor.commands.setContent(recommendations[newIndex]);
    },
    [currentRecommendationIndex, recommendations, editor, saveCurrentEditorToRecommendation]
  );

  const showNextRecommendation = () => navigateToRecommendation('next');
  const showPreviousRecommendation = () => navigateToRecommendation('previous');

  const handleOpenManagePromptTypes = () => setIsManagePromptTypeOpen(true);
  const handleCloseManagePromptTypes = () => setIsManagePromptTypeOpen(false);

  /**
   * Handles the cancel action in the prompt editor.
   * Clears unsaved recommendations and exits edit mode or closes the drawer.
   */
  const handleCancel = useCallback(() => {
    // Clear unsaved recommendations
    setRecommendations([]);
    setCurrentRecommendationIndex(null);

    // Exit editing mode or close drawer if no promptId
    if (!promptId) {
      closeDrawer();
      return;
    }

    setIsEditing(false);
    // Reset the prompt editor to the original prompt
    if (editor && originalPrompt?.prompt) {
      setEditorContent(originalPrompt.prompt);
    }
    // Force remount of FinalForm to reset initialValues
    setFormKey((prev) => prev + 1);
  }, [editor, originalPrompt, promptId, closeDrawer, setEditorContent]);

  const promptType = useMemo(() => {
    if (!originalPrompt?.promptTypeId || !promptTypes) return null;
    return promptTypes.find((type) => type.id === originalPrompt.promptTypeId) || null;
  }, [originalPrompt?.promptTypeId, promptTypes]);

  const handleConfirm = useCallback(
    async (passedPrompt?: CreatePrompt) => {
      setIsSaving(true);
      let result;
      let isNew = false;
      let attachedFiles: CustomFile[] = [];
      let clonedFiles: CustomFile[] = [];
      const {
        id: _id,
        createdAt: _createdAt,
        parentCreatedAt: _parentCreatedAt,
        createdBy: _createdBy,
        updatedBy: _updatedBy,
        deleted: _deleted,
        fullCount: _fullCount,
        createdByName: _createdByName,
        ownerName: _ownerName,
        contributors: _contributors,
        sharedWith: _sharedWith,
        archived: _archived,
        files: promptFiles,
        ...adminPayload
      } = { ...originalPrompt, ...(passedPrompt || currentPrompt) } as CreatePrompt &
        Partial<Prompt> & {
          contributors?: string[];
        };

      const { featured: _featured, ...nonAdminPayload } = adminPayload;
      const payload = isAdmin ? adminPayload : nonAdminPayload;

      const filesForCopy =
        shouldSaveCopyRef.current && Array.isArray(promptFiles)
          ? promptFiles.map((file: CustomFile) => ({ ...file, userId: currentUser?.id }))
          : existingFiles;

      try {
        if (uploadedFiles.length > 0) {
          const { processed, hadFailures } = await processFiles(uploadedFiles, false);
          if (hadFailures) return;
          attachedFiles = processed;
        }

        if (filesForCopy.length > 0) {
          const { processed, hadFailures } = await processFiles(filesForCopy, true);
          if (hadFailures) return;
          clonedFiles = processed;
        }

        if (!shouldSaveCopyRef.current && promptId) {
          analytics.event(
            AnalyticsEvent.WM_NIGEL_PROMPT_UPDATE,
            { id: promptId },
            { promptId, promptView }
          );

          const update = isAdmin ? updatePromptAdmin : updatePrompt;
          const newPromptVersion = update({
            prompt: payload as PatchPromptOpts,
            promptId,
          });

          result = await dispatch(newPromptVersion).unwrap();
        } else {
          delete payload.parent_id;

          if (selectedApp === AppEnum.DEAL_GPT) {
            payload.labels = [...new Set([INTELLIO_ADVANTAGE_LABEL, ...(payload.labels ?? [])])];
          }

          if (isAIAppStore) {
            payload.labels = [...new Set([AI_APP_STORE_LABEL, ...(payload.labels ?? [])])];
          } else if (external) {
            payload.labels = [...new Set([EXTERNAL_RESOURCE_LABEL, ...(payload.labels ?? [])])];
          }

          isNew = true;

          if (!shouldSaveCopyRef.current) {
            analytics.event(
              AnalyticsEvent.WM_NIGEL_PROMPT_CREATE,
              { id: currentFolder?.id },
              { folderId: currentFolder?.id }
            );
          }

          const create = isAdmin ? createPromptAdmin : createPrompt;
          const newPrompt = create(payload as CreatePrompt);

          result = await dispatch(newPrompt).unwrap();
        }

        if (result?.id) {
          const allProcessedFiles = [...attachedFiles, ...clonedFiles];

          for (const file of allProcessedFiles) {
            try {
              await dispatch(
                updateFile({
                  fileId: file.id,
                  file: {
                    promptId: result.id,
                    lastModified: getFormattedDBTimestamp(),
                  },
                })
              );
            } catch (err) {
              enqueueSnackbar(`Failed to update metadata for file: ${file.fileName}`, {
                variant: 'error',
                autoHideDuration: 4000,
              });
            }
          }

          //TODO: refactor this once ticket for error handling on enritchedFetch is worked on.
          isNew ? onNewPrompt?.(result) : onEditPrompt?.(result);
          if (isNew) closeDrawer();

          enqueueSnackbar(
            isNew && isExternalPrompt
              ? 'External prompt saved. It will show up in the new Nigel library.'
              : `${entity} saved!`,
            { variant: 'success', autoHideDuration: 4000 }
          );

          // Clear unsaved recommendations
          setRecommendations([]);
          setCurrentRecommendationIndex(null);
          setIsEditing(false);
          setUploadedFiles([]);
        } else {
          for (const file of [...attachedFiles, ...clonedFiles]) {
            try {
              await dispatch(deleteFile(file.id)).unwrap();
            } catch (err) {
              enqueueSnackbar(`Failed to delete file.`, {
                variant: 'error',
                autoHideDuration: 4000,
              });
            }
          }

          enqueueSnackbar(`Failed to save ${entity}.`, {
            variant: 'error',
            autoHideDuration: 4000,
          });
        }
      } catch (error) {
        const message = (error as string) || (error as Error)?.message;
        if (message.startsWith('A prompt for this prompt type already exists')) {
          enqueueSnackbar(message, { variant: 'error' });
        } else {
          enqueueSnackbar('An unexpected error occurred. Please try again.', {
            variant: 'error',
            autoHideDuration: 4000,
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      dispatch,
      promptId,
      originalPrompt,
      currentPrompt,
      isAdmin,
      closeDrawer,
      onNewPrompt,
      onEditPrompt,
      analytics,
      currentFolder,
      promptView,
      existingFiles,
      uploadedFiles,
      processFiles,
      currentUser?.id,
      isExternalPrompt,
      selectedApp,
      isAIAppStore,
      entity,
      external,
    ]
  );

  const handleTextChange = (index: number, newText: string) => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_FILE_TEXT_CHANGED,
      { id: existingFiles[index].id, value: newText },
      {
        fileId: existingFiles[index].id,
        newFileText: newText,
      }
    );
    dispatch(
      updateFile({
        fileId: existingFiles[index].id,
        file: {
          contents: newText,
          lastModified: getFormattedDBTimestamp(),
          status: 'Modified By User',
        },
      })
    );
  };
  const onSubmit = useCallback(
    (values: CreatePrompt) => {
      saveCurrentEditorToRecommendation();
      const promptToSave =
        currentRecommendationIndex !== null && recommendations.length > 0
          ? recommendations[currentRecommendationIndex]
          : editor?.getText().trim() ?? '';

      let tools = values.tools || [];
      if (tools.includes(ToolName.DEEP_RESEARCH)) {
        tools = tools.filter((tool) => ToolConfig[tool].deepResearchSupport);
      }

      const newPrompt = {
        title: shouldSaveCopyRef.current ? values['title'] + ' Copy' : values['title'],
        description: values['description'],
        prompt: promptToSave,
        published: shouldSaveCopyRef.current ? false : values['published'],
        userEditRequired: values['userEditRequired'],
        metadata: values['metadata'],
        labels: values['labels'],
        tools,
        appName: selectedApp,
        featured: shouldSaveCopyRef.current ? false : values['featured'],
        variables: selectedText,
        ownerId: shouldSaveCopyRef.current
          ? currentUser?.id ?? values['ownerId']
          : values['ownerId'],
        solutionType: values['solutionType'],
        referenceLink: values['referenceLink'],
        promptTypeId: selectedPromptType?.id,
        systemPrompt: values['systemPrompt'],
        vectorStoreId: values['vectorStoreId'],
        externalLink: values['externalLink'],
      } as CreatePrompt;
      setCurrentPrompt(newPrompt);

      if (
        (currentPrompt?.published && !shouldSaveCopyRef.current) ||
        (!currentPrompt?.published && newPrompt.published)
      ) {
        if ((currentPrompt?.published === false || !prompt) && newPrompt.published) {
          setModalTitle(`Are you sure you want to publish this ${entity}?`);
        } else {
          setModalTitle(`Are you sure you want to update this published ${entity}?`);
        }
        setIsModificationConfirmationOpen(true);
      } else {
        handleConfirm(newPrompt);
      }
    },
    [
      entity,
      selectedApp,
      editor,
      selectedText,
      currentPrompt,
      currentRecommendationIndex,
      recommendations,
      currentUser?.id,
      selectedPromptType?.id,
      handleConfirm,
      saveCurrentEditorToRecommendation,
    ]
  );

  const shouldShowFeatured = useCallback(
    (form: FormApi<CreatePrompt, Partial<CreatePrompt>>) => {
      if (showFeatured) {
        form.change('featured', false);
        setShowFeatured(false);
      } else {
        setShowFeatured(true);
      }
    },
    [showFeatured]
  );

  const executePrompt = useCallback(() => {
    if (!promptId) return;

    analytics.event(
      AnalyticsEvent.WM_NIGEL_PROMPT_CLICK,
      {
        id: promptId,
        value: promptView,
      },
      {
        promptId: promptId,
        promptView: promptView,
        isExternal: isExternalPrompt,
      }
    );

    closeDrawer();

    if (isExternalPrompt && originalPrompt?.externalLink) {
      openSafeExternalLink(originalPrompt.externalLink);
      return;
    }

    const query = new URLSearchParams({ prompt: promptId });

    navigate(`/folder/${currentFolder?.id ?? myFolder.id}/new-chat?${query.toString()}`);
  }, [
    promptId,
    promptView,
    currentFolder,
    analytics,
    closeDrawer,
    navigate,
    isExternalPrompt,
    originalPrompt?.externalLink,
  ]);

  const onCopyToMyPrompts = useCallback(() => {
    if (!promptId) return;

    analytics.event(
      AnalyticsEvent.WM_NIGEL_PROMPT_COPY,
      {
        id: promptId,
      },
      {
        promptId: promptId,
        promptView: promptView,
      }
    );

    shouldSaveCopyRef.current = true;
  }, [analytics, promptId, promptView]);

  const onArchivePrompt = useCallback(
    (value: boolean) => {
      if (!promptId) return;
      setIsArchiving(false);

      analytics.event(
        AnalyticsEvent.WM_NIGEL_PROMPT_ARCHIVE,
        {
          id: promptId,
        },
        {
          promptId: promptId,
          promptView: promptView,
          archived: value,
        }
      );

      dispatch(archivePrompt({ promptId, value }));
      closeDrawer();
    },
    [promptId, dispatch, closeDrawer, analytics, promptView]
  );

  const updateTools = (newTools: ToolName[], form: FormApi<CreatePrompt>) => {
    if (newTools.includes(ToolName.DEEP_RESEARCH) && !newTools.includes(ToolName.WEB_SEARCH)) {
      newTools.push(ToolName.WEB_SEARCH);
    }
    form.change('tools', newTools);
  };

  const isUserAllowedToEditPrompt = useMemo(() => {
    const isNewPrompt = promptId === undefined;
    if (isNewPrompt) return true;

    if (!currentUser || !originalPrompt) return false;

    const isOwner = originalPrompt.ownerId === currentUser.id;
    const isSharedWithUser = Object.keys(originalPrompt.sharedWith ?? {}).includes(currentUser.id);
    return isAdmin || isOwner || isSharedWithUser;
  }, [promptId, originalPrompt, currentUser, isAdmin]);

  useEffect(() => {
    if (originalPrompt) {
      setCurrentPrompt(originalPrompt);
      setSelectedText(originalPrompt.variables);
      setEditingIsRequired(originalPrompt.userEditRequired);
      setEditorContent(originalPrompt.prompt);
      setExistingFiles(originalPrompt.files || []);
      if (originalPrompt.promptTypeId) {
        const type = promptTypes?.find(
          (promptType) => promptType.id === originalPrompt.promptTypeId
        );
        setSelectedPromptType(type ?? null);
      }
    }
  }, [originalPrompt, promptTypes, setEditorContent]);

  useEffect(() => {
    if (!editingIsRequired) {
      setSelectedText([]);
    }
  }, [editingIsRequired]);

  useEffect(() => {
    setShowFeatured(currentPrompt?.published);
  }, [currentPrompt?.published]);

  // If promptId is provided and there's no originalPrompt, then we are loading the prompt
  if (promptId && !originalPrompt) return <PromptFormSkeleton />;

  return (
    <>
      <LoadingMask isLoading={isSaving} />
      <FinalForm
        key={formKey}
        initialValues={initialPromptValues}
        validate={validate}
        onSubmit={onSubmit}
        render={({ handleSubmit, form, values }) => (
          <form
            style={{
              display: 'flex',
              flexGrow: 1,
              textAlign: 'left',
              flexDirection: 'column',
            }}
            onSubmit={handleSubmit}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '32px 32px 0 32px',
                gap: isExternalPrompt ? 4 : 2,
                flexGrow: 1,
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 300px)', // 125px for the header + 50px for the tabs + 80 px for the footer + 25 px padding top
              }}
            >
              {fileToView && fileViewIndex !== undefined && (
                <FileViewDialog
                  open={true}
                  file={fileToView}
                  onClose={() => setFileToView(undefined)}
                  handleTextChange={(newText) => {
                    handleTextChange(fileViewIndex, newText);
                  }}
                  llm={selectedApp === AppEnum.DEAL_GPT ? LlmEnum.OPENAI_RAG : llm}
                  isViewOnly={isEditing ? false : true}
                />
              )}
              {isAdmin && !isExternalPrompt && isEditing && (
                <Box sx={{ marginBottom: 2 }}>
                  <Autocomplete
                    value={selectedPromptType}
                    onChange={(_e, newValue) => setSelectedPromptType(newValue)}
                    options={promptTypes ?? []}
                    freeSolo={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="promptType"
                        label="Prompt Type"
                        placeholder="Select a prompt type"
                        variant="standard"
                      />
                    )}
                    sx={{ mb: 1 }}
                  />
                  <LinkDiv sx={{ ml: 0 }} onClick={handleOpenManagePromptTypes}>
                    <Settings fontSize="small" sx={{ marginRight: 0.5 }} />
                    <Typography variant="body2" component="span">
                      Manage Prompt Types
                    </Typography>
                  </LinkDiv>
                </Box>
              )}

              {isEditing && (
                <>
                  <Autocomplete
                    value={values.solutionType ?? null}
                    onChange={(_e, newValue) => {
                      form.change('solutionType', newValue ?? undefined);
                    }}
                    options={solutionTypeOptions}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        label="Solution Type"
                        placeholder="Select a solution type"
                        required
                        variant="standard"
                      />
                    )}
                    disabled={!isUserAllowedToEditPrompt}
                    sx={{ mb: 1 }}
                  />
                  <Autocomplete
                    value={userOptions.find((user) => user.id === values.ownerId) ?? null}
                    onChange={(_e, newValue) => {
                      form.change('ownerId', newValue?.id ?? '');
                    }}
                    options={userOptions}
                    getOptionLabel={(option) => `${option.preferredName} (${option.email})`}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        label="Owner"
                        placeholder="Select an owner"
                        required
                        variant="standard"
                      />
                    )}
                    disabled={!isUserAllowedToEditPrompt}
                    sx={{ mb: 1 }}
                  />
                </>
              )}

              {isEditing && (
                <TextField
                  label="Title"
                  name="title"
                  helperText="ex: Generate questions from notes..."
                  required
                  InputLabelProps={{ shrink: true }}
                  disabled={!isUserAllowedToEditPrompt}
                  inputProps={{ maxLength: 250 }}
                />
              )}

              {!isEditing && (
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Typography fontWeight="bold">Solution Type:</Typography>
                  <Typography>{originalPrompt?.solutionType ?? SolutionType.PROMPT}</Typography>
                </Box>
              )}

              {!isEditing && (
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Typography fontWeight="bold">Owner:</Typography>
                  <Typography>
                    {originalPrompt?.ownerName ?? originalPrompt?.createdByName}
                  </Typography>
                </Box>
              )}

              {!isEditing && promptType && (
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Typography fontWeight="bold">Prompt Type:</Typography>
                  <Typography>{promptType.label}</Typography>
                </Box>
              )}

              {isEditing ? (
                <TextField
                  label="Description"
                  name="description"
                  helperText={`ex: Use this ${entity} to generate notes...`}
                  required
                  multiline
                  minRows={2}
                  InputLabelProps={{ shrink: true }}
                  disabled={!isUserAllowedToEditPrompt}
                />
              ) : (
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Typography fontWeight="bold">Description:</Typography>
                  <Typography>{originalPrompt?.description}</Typography>
                </Box>
              )}
              {isAdmin && !isExternalPrompt && (
                <>
                  {isEditing ? (
                    <TextField
                      label="System Prompt"
                      name="systemPrompt"
                      multiline
                      minRows={2}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isUserAllowedToEditPrompt}
                    />
                  ) : (
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Typography fontWeight="bold">System prompt:</Typography>
                      <Typography>{originalPrompt?.systemPrompt}</Typography>
                    </Box>
                  )}
                </>
              )}
              {isEditing ? (
                <div style={{ display: 'flex' }}>
                  <TextField
                    label="Tips and Tricks"
                    name="referenceLink"
                    InputLabelProps={{ shrink: true }}
                    disabled={!isUserAllowedToEditPrompt}
                  />
                  <LearnMore
                    link="https://howto.westmonroe.com/a/1960709-adding-a-tips-tricks-guide-to-my-prompt"
                    sx={{ width: 'min-content', mr: '5px', ml: '10px' }}
                  />
                </div>
              ) : (
                originalPrompt?.referenceLink && (
                  <Box
                    display="flex"
                    alignItems="baseline"
                    gap={1}
                    sx={{
                      '&:hover': {
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationColor: '#0047FF',
                      },
                    }}
                  >
                    <Typography
                      sx={{ color: '#0047FF', display: 'flex', alignItems: 'center' }}
                      onClick={() => {
                        window.open(
                          originalPrompt.referenceLink?.startsWith('http')
                            ? originalPrompt.referenceLink
                            : `https://${originalPrompt.referenceLink}`,
                          '_blank'
                        );
                      }}
                    >
                      <TipsAndUpdatesOutlined sx={{ height: '16px' }} />
                      Tips and Tricks
                    </Typography>
                  </Box>
                )
              )}
              {isExternalPrompt && isEditing && (
                <TextField
                  label="External Link"
                  name="externalLink"
                  helperText="After you save this external prompt, it will show up in the new Nigel library."
                  InputLabelProps={{ shrink: true }}
                  disabled={!isUserAllowedToEditPrompt}
                  required
                />
              )}
              {!isExternalPrompt &&
                (isEditing ? (
                  <Box>
                    <PromptEditTiptapWrapper sx={{ position: 'relative' }}>
                      <PromptEditTiptapLabel>Prompt *</PromptEditTiptapLabel>
                      <PromptEditTiptapStyled editor={editor} />
                      <Tooltip
                        title="Generate a new prompt suggestion based on your current input. You can create multiple variations and switch between them."
                        arrow
                      >
                        <IconButton
                          onClick={createRecommendation}
                          disabled={isGeneratingRecommendation}
                          sx={{
                            p: 0,
                            position: 'absolute',
                            bottom: '15px',
                            right: '10px',
                          }}
                        >
                          <AutoFixHigh
                            sx={{
                              fontSize: 28,
                              cursor: 'pointer',
                              color: isGeneratingRecommendation ? '#ccc' : '#070154',
                              '&:hover': {
                                color: '#115293',
                              },
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      {recommendations.length > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: '10px',
                            left: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Tooltip title="Previous recommendation" arrow>
                            <IconButton
                              onClick={showPreviousRecommendation}
                              disabled={isAtFirstRecommendation}
                              sx={{
                                p: 0,
                                color: '#5A6B8C',
                                cursor: 'pointer',
                                '&:hover': {
                                  color: '#2f3b53',
                                },
                              }}
                            >
                              <ArrowCircleLeftOutlined fontSize="large" />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="body2">
                            Draft{' '}
                            {currentRecommendationIndex !== null
                              ? currentRecommendationIndex + 1
                              : 0}{' '}
                            of {recommendations.length}
                          </Typography>
                          <Tooltip title="Next recommendation" arrow>
                            <IconButton
                              onClick={showNextRecommendation}
                              disabled={isAtLastRecommendation}
                              sx={{
                                p: 0,
                                color: '#070154',
                                cursor: 'pointer',
                                '&:hover': {
                                  color: '#2f3b53',
                                },
                              }}
                            >
                              <ArrowCircleRightOutlined fontSize="large" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </PromptEditTiptapWrapper>
                    <PromptEditHelperText>
                      ex: Generate a bulleted list with the most important action items from these
                      notes...
                    </PromptEditHelperText>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Typography fontWeight="bold" minWidth="70px">
                      Prompt:
                    </Typography>
                    <Typography
                      sx={{
                        whiteSpace: 'pre-wrap', // preserves line breaks and wraps text
                        wordBreak: 'break-word', // avoids overflow from long words
                      }}
                    >
                      {originalPrompt?.prompt}
                    </Typography>
                  </Box>
                ))}
              {/* Be careful, DO NOT ADD components here, Bubble Menu should be next to PromptEditTiptapwrapper */}
              {!isExternalPrompt && editor && (
                <BubbleMenu editor={editingIsRequired ? editor : null}>
                  <button
                    type="button"
                    style={{
                      fontSize: '15px',
                      padding: '5px',
                      margin: '5px',
                    }}
                    onClick={() => {
                      addSelectedVariable();
                    }}
                  >
                    Create Variable
                  </button>
                </BubbleMenu>
              )}
              {!isExternalPrompt && isEditing && (
                <PromptFileUpload
                  existingFiles={existingFiles}
                  setExistingFiles={setExistingFiles}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  onFileView={(file) => {
                    setFileToView(file);
                    setFileViewIndex(
                      originalPrompt?.files?.findIndex((f) => f.id === file.id) ?? undefined
                    );
                  }}
                />
              )}
              <Autocomplete
                multiple
                readOnly={!isEditing}
                id="labels-standard"
                disabled={!isUserAllowedToEditPrompt}
                options={tagOptions}
                value={values.labels}
                onChange={(_, newValue) => {
                  const newTags = permanentTags.reduce((prevTags, tag) => {
                    if (values.labels.includes(tag) && !newValue.includes(tag)) {
                      prevTags.push(tag);
                    }

                    return prevTags;
                  }, newValue);

                  form.change('labels', newTags);
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  const inputValue = (e.target as HTMLInputElement).value.trim();
                  if (
                    e.key === 'Tab' &&
                    inputValue &&
                    !values.labels.includes(inputValue) &&
                    tagOptions.includes(inputValue)
                  ) {
                    e.preventDefault();
                    form.change('labels', [...values.labels, inputValue]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    inputProps={{
                      ...params.inputProps,
                      required: values['labels'].length === 0,
                      onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        const inputValue = (e.target as HTMLInputElement).value.trim();
                        if (e.key === 'Enter' && !tagOptions.includes(inputValue)) {
                          e.preventDefault();
                        }
                      },
                    }}
                    name="tagInput"
                    variant="standard"
                    label="Labels"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    return (
                      <Chip
                        {...tagProps}
                        key={option}
                        label={option}
                        onDelete={!permanentTags.includes(option) ? tagProps.onDelete : undefined}
                      />
                    );
                  })
                }
              />
              <p
                style={{
                  fontSize: '0.75rem',
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontWeight: '400',
                  letterSpacing: '0.03333em',
                  marginLeft: '14px',
                  lineHeight: '1.66',
                  marginTop: '0',
                  marginBottom: '0',
                }}
              >
                Be sure to tag with the appropriate model if needed. You need to select at least one
                tag.
              </p>

              {!isExternalPrompt && (
                <>
                  <PromptToolsSelector
                    values={values}
                    options={toolOptions}
                    readOnly={!isEditing}
                    disabled={!isUserAllowedToEditPrompt}
                    onChange={(newTools) => updateTools(newTools, form)}
                    onDelete={(newTools) => form.change('tools', newTools)}
                  />
                  <Switches
                    disabled={!isUserAllowedToEditPrompt || !isEditing}
                    helperText={`If toggled on, the prompt text will appear in the message field to allow editing before sending. If toggled off, the prompt will automatically be triggered to send once the user clicks the prompt card.
                    Highlight text with your mouse in the prompt field to add variables to the prompt.`}
                    name={'userEditRequired'}
                    data={{ label: 'User Edit Required?', value: false }}
                    onClick={() => setEditingIsRequired(!editingIsRequired)}
                  />
                  {selectedText?.length > 0 && (
                    <div>
                      <h4>Prompt Variables</h4>
                      {selectedText.map((chip, index) => (
                        <Chip
                          key={index}
                          label={chip}
                          onDelete={isEditing ? () => handleDeleteChip(index) : undefined}
                          style={{ margin: '2px' }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {isEditing && (
                <>
                  <Switches
                    disabled={!isUserAllowedToEditPrompt}
                    helperText={`This controls whether or not the ${entity} is published for communal use`}
                    name={'published'}
                    onClick={() => shouldShowFeatured(form)}
                    data={{ label: `Publish this ${entity}?`, value: false }}
                  />
                  {isAdmin && showFeatured && (
                    <>
                      <Switches
                        helperText={`This controls whether or not the ${entity} is featured for communal use`}
                        name={'featured'}
                        data={{
                          label: `Make this ${entity} featured?`,
                          value: false,
                        }}
                      />
                    </>
                  )}
                </>
              )}

              {!isExternalPrompt && isEditing && (
                <StyledAccordion sx={{ marginY: '15px !important' }}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel-content"
                    id="panel-header"
                  >
                    <Typography>Advanced Settings</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Field name="metadata.temperature">
                      {({ input }) => (
                        <TemperatureSelector
                          onTemperatureChange={input.onChange}
                          currentTempValue={input.value}
                          disabled={!isUserAllowedToEditPrompt}
                        />
                      )}
                    </Field>
                    {isAdmin && (
                      <TextField
                        sx={{ mt: '10px' }}
                        label="Vector Store ID"
                        name="vectorStoreId"
                        InputLabelProps={{ shrink: true }}
                        disabled={!isUserAllowedToEditPrompt}
                      />
                    )}
                  </AccordionDetails>
                </StyledAccordion>
              )}

              {!isExternalPrompt && !isEditing && (
                <Box display="flex" flexDirection="column" gap={2}>
                  {(originalPrompt?.files?.length ?? 0) > 0 && (
                    <Box>
                      <Typography fontWeight="bold">Files</Typography>
                      <List>
                        {originalPrompt?.files?.map((file: CustomFile, i: number) => (
                          <ListItem
                            key={i}
                            sx={{
                              maxWidth: 320,
                              width: '100%',
                            }}
                          >
                            <Box
                              onClick={() => {
                                setFileToView(file);
                                setFileViewIndex(i);
                              }}
                              style={{
                                cursor: 'pointer',
                              }}
                            >
                              <FileCard
                                onPromptForm={true}
                                isStarterFile={true}
                                uploadedFile={file}
                              />
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                height: '80px',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: '1px solid #ddd',
                px: 2,
              }}
            >
              {/* Left-aligned actions */}
              <Box>
                {isUserAllowedToEditPrompt && !isEditing && (
                  <Button variant="outlined" onClick={() => setIsArchiving(true)}>
                    {originalPrompt?.archived ? 'Unarchive' : 'Archive'}
                  </Button>
                )}
              </Box>

              {/* Right-aligned actions */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {!isEditing && !isExternalPrompt && originalPrompt && !originalPrompt.archived && (
                  <Button
                    type="submit"
                    onClick={onCopyToMyPrompts}
                    variant="outlined"
                    color="success"
                  >
                    Copy To My Prompts
                  </Button>
                )}

                {isEditing ? (
                  <>
                    <Button type="button" variant="outlined" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      id="save-button"
                      type="submit"
                      onClick={() => {
                        shouldSaveCopyRef.current = false;
                      }}
                      variant="contained"
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    {isUserAllowedToEditPrompt && !originalPrompt?.archived && (
                      <Button onClick={() => setIsEditing(true)} variant="outlined">
                        Edit
                      </Button>
                    )}
                    <Button onClick={executePrompt} variant="contained">
                      Try it
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </form>
        )}
      />
      {originalPrompt && isArchiving && (
        <ConfirmationDialog
          title={`${originalPrompt.archived ? 'Unarchive' : 'Archive'} Prompt?`}
          message={`Are you sure you want to ${
            originalPrompt.archived ? 'unarchive' : 'archive'
          } this prompt?`}
          close={() => setIsArchiving(false)}
          confirm={() => onArchivePrompt(!originalPrompt.archived)}
        />
      )}
      <ModificationConfirmationDialog
        onConfirm={() => handleConfirm()}
        onCancel={() => {
          setIsModificationConfirmationOpen(false);
        }}
        isOpen={isModificationConfirmationOpen}
        setIsOpen={setIsModificationConfirmationOpen}
        title={modalTile}
        content="Everyone will be able to see your changes. Unlike chats, prompts are public."
      />
      <ManagePromptTypesDialog
        open={isManagePromptTypeOpen}
        onClose={handleCloseManagePromptTypes}
        promptTypes={promptTypes}
        refetchPromptTypes={refetchPromptTypes}
      />
    </>
  );
};

export default PromptForm;
